import { useState, useRef, useEffect, useCallback } from "react";
import { cn } from "../../lib/utils";
import { OwnerTag } from "./OwnerTag";
import { IconFolder, IconDeleteX } from "nucleo-glass";
import { NucleoIcon } from "./NucleoIcon";
import { HugeiconsIcon } from "@hugeicons/react";
import { PencilEdit01Icon } from "@hugeicons/core-free-icons";
import type { Owner } from "../../data/types";

// ── Props ───────────────────────────────────────────────────────

interface FolderCardProps {
  id: string;
  name: string;
  owner: Owner;
  itemCount: number;
  viewMode: "grid" | "list";
  onOpen: (id: string) => void;
  onRename: (id: string, newName: string) => void;
  onDelete: (id: string) => void;
  onDropAsset?: (assetId: string, folderId: string) => void;
}

// ── Component ───────────────────────────────────────────────────

export function FolderCard({
  id,
  name,
  owner,
  itemCount,
  viewMode,
  onOpen,
  onRename,
  onDelete,
  onDropAsset,
}: FolderCardProps) {
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(name);
  const [isDragOver, setIsDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0);

  useEffect(() => {
    if (isRenaming) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isRenaming]);

  function handleRenameSubmit() {
    const trimmed = renameValue.trim();
    if (trimmed && trimmed !== name) {
      onRename(id, trimmed);
    } else {
      setRenameValue(name);
    }
    setIsRenaming(false);
  }

  function handleDeleteClick(e: React.MouseEvent) {
    e.stopPropagation();
    onDelete(id);
  }

  function handleRenameClick(e: React.MouseEvent) {
    e.stopPropagation();
    setRenameValue(name);
    setIsRenaming(true);
  }

  // ── Drag-and-drop handlers ──
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer.types.includes("application/x-asset-id")) {
      setIsDragOver(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragOver(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.types.includes("application/x-asset-id")) {
      e.dataTransfer.dropEffect = "move";
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragOver(false);

    const assetId = e.dataTransfer.getData("application/x-asset-id");
    if (assetId && onDropAsset) {
      onDropAsset(assetId, id);
    }
  }, [id, onDropAsset]);

  // ── List view ──
  if (viewMode === "list") {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={() => onOpen(id)}
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onOpen(id);
          }
        }}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className={cn(
          "w-full flex items-center gap-[var(--spacing-3)] px-[var(--spacing-3)] py-[var(--spacing-2)]",
          "rounded-[var(--radius-md)] border border-[var(--color-border-stroke)]",
          "bg-[var(--color-bg-surface)] hover:bg-[var(--color-bg-hover)]",
          "transition-all duration-[var(--duration-fast)] cursor-pointer text-left group",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)] focus-visible:ring-offset-2",
          isDragOver && "drag-over-folder",
        )}
      >
        {/* Folder icon */}
        <div className={cn(
          "w-[40px] h-[40px] rounded-[var(--radius-sm)] shrink-0 flex items-center justify-center",
          "transition-colors duration-[var(--duration-fast)]",
          isDragOver ? "bg-[var(--color-accent-200)]/30" : "bg-[var(--color-accent-subtle-bg)]/50",
        )}>
          <span className={cn(
            "transition-colors duration-[var(--duration-fast)]",
            isDragOver ? "text-[var(--color-accent-500)]" : "text-[var(--color-accent-400)]",
          )}>
            <NucleoIcon icon={<IconFolder size={20} />} />
          </span>
        </div>

        {/* Name */}
        <div className="flex-1 min-w-0">
          {isRenaming ? (
            <input
              ref={inputRef}
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onBlur={handleRenameSubmit}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleRenameSubmit();
                if (e.key === "Escape") {
                  setRenameValue(name);
                  setIsRenaming(false);
                }
              }}
              onClick={(e) => e.stopPropagation()}
              className={cn(
                "w-full h-[24px] px-[var(--spacing-1_5)] text-[14px] font-medium",
                "text-[var(--color-text-primary)] bg-[var(--color-bg-canvas)]",
                "border border-[var(--color-accent-focus)] rounded-[var(--radius-sm)]",
                "focus:outline-none",
              )}
            />
          ) : (
            <>
              <p className="text-[14px] font-medium text-[var(--color-text-primary)] truncate">
                {name}
                {isDragOver && (
                  <span className="text-[var(--color-accent-500)] ml-[var(--spacing-1)]">
                    ← Drop here
                  </span>
                )}
              </p>
              <p className="text-[13px] text-[var(--color-text-muted)]">
                {itemCount} item{itemCount !== 1 ? "s" : ""}
              </p>
            </>
          )}
        </div>

        {/* Meta + actions */}
        <div className="flex items-center gap-[var(--spacing-2)] shrink-0">
          <OwnerTag owner={owner} />
          <div className="flex items-center gap-[var(--spacing-1)] opacity-0 group-hover:opacity-100 transition-opacity duration-[var(--duration-fast)]">
            <button
              onClick={handleRenameClick}
              className={cn(
                "p-[var(--spacing-1)] rounded-[var(--radius-sm)]",
                "text-[var(--color-icon)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)]",
                "transition-colors duration-[var(--duration-fast)]",
              )}
              aria-label="Rename folder"
            >
              <HugeiconsIcon icon={PencilEdit01Icon} size={14} />
            </button>
            <button
              onClick={handleDeleteClick}
              className={cn(
                "p-[var(--spacing-1)] rounded-[var(--radius-sm)]",
                "text-[var(--color-icon)] hover:text-red-400 hover:bg-red-500/10",
                "transition-colors duration-[var(--duration-fast)]",
              )}
              aria-label="Delete folder"
            >
              <NucleoIcon icon={<IconDeleteX size={14} />} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Grid view ──
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(id)}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(id);
        }
      }}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      className={cn(
        "flex flex-col rounded-[var(--radius-lg)] border border-[var(--color-border-stroke)]",
        "bg-[var(--color-bg-surface)] hover:bg-[var(--color-bg-hover)]",
        "transition-all duration-[var(--duration-fast)] cursor-pointer text-left overflow-hidden group",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)] focus-visible:ring-offset-2",
        isDragOver && "drag-over-folder",
      )}
    >
      {/* Folder icon area */}
      <div className={cn(
        "relative w-full aspect-[4/3] flex items-center justify-center",
        "transition-colors duration-[var(--duration-fast)]",
        isDragOver ? "bg-[var(--color-accent-subtle-bg)]" : "bg-[var(--color-accent-subtle-bg)]/30",
      )}>
        <span className={cn(
          "transition-all duration-[var(--duration-medium)]",
          isDragOver
            ? "text-[var(--color-accent-500)] opacity-90 scale-110"
            : "text-[var(--color-accent-400)] opacity-50 scale-100",
        )}>
          <NucleoIcon icon={<IconFolder size={48} />} />
        </span>

        {/* Drop hint */}
        {isDragOver && (
          <div className="absolute bottom-[var(--spacing-2)] left-1/2 -translate-x-1/2">
            <span className="text-[11px] font-medium text-[var(--color-accent-600)] bg-[var(--color-accent-100)]/80 px-[var(--spacing-2)] py-[var(--spacing-0_5)] rounded-[var(--radius-sm)] backdrop-blur-sm border border-[var(--color-accent-400)]/20">
              Drop to move
            </span>
          </div>
        )}

        {/* Action overlay */}
        <div
          className={cn(
            "absolute top-[var(--spacing-2)] right-[var(--spacing-2)] flex gap-[var(--spacing-1)]",
            "opacity-0 group-hover:opacity-100 transition-opacity duration-[var(--duration-fast)]",
          )}
        >
          <button
            onClick={handleRenameClick}
            className={cn(
              "p-[var(--spacing-1_5)] rounded-[var(--radius-sm)]",
              "bg-[var(--color-bg-canvas)]/80 backdrop-blur-sm",
              "text-[var(--color-icon)] hover:text-[var(--color-text-primary)]",
              "border border-[var(--color-border-stroke)]",
              "transition-colors duration-[var(--duration-fast)]",
            )}
            aria-label="Rename folder"
          >
            <HugeiconsIcon icon={PencilEdit01Icon} size={14} />
          </button>
          <button
            onClick={handleDeleteClick}
            className={cn(
              "p-[var(--spacing-1_5)] rounded-[var(--radius-sm)]",
              "bg-[var(--color-bg-canvas)]/80 backdrop-blur-sm",
              "text-[var(--color-icon)] hover:text-red-400",
              "border border-[var(--color-border-stroke)]",
              "transition-colors duration-[var(--duration-fast)]",
            )}
            aria-label="Delete folder"
          >
            <NucleoIcon icon={<IconDeleteX size={14} />} />
          </button>
        </div>
      </div>

      {/* Card body */}
      <div className="p-[var(--spacing-3)] flex flex-col gap-[var(--spacing-1)]">
        {isRenaming ? (
          <input
            ref={inputRef}
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onBlur={handleRenameSubmit}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleRenameSubmit();
              if (e.key === "Escape") {
                setRenameValue(name);
                setIsRenaming(false);
              }
            }}
            onClick={(e) => e.stopPropagation()}
            className={cn(
              "w-full h-[28px] px-[var(--spacing-2)] text-[14px] font-medium",
              "text-[var(--color-text-primary)] bg-[var(--color-bg-canvas)]",
              "border border-[var(--color-accent-focus)] rounded-[var(--radius-sm)]",
              "focus:outline-none",
            )}
          />
        ) : (
          <p className="text-[14px] font-medium text-[var(--color-text-primary)] truncate">{name}</p>
        )}
        <div className="flex items-center gap-[var(--spacing-2)]">
          <span className="text-[12px] text-[var(--color-text-muted)]">
            {itemCount} item{itemCount !== 1 ? "s" : ""}
          </span>
          <OwnerTag owner={owner} />
        </div>
      </div>
    </div>
  );
}
